import { Media, Territory } from '../static';
import { sum } from '../utils';
import { Action, GroupChild, PaymentAction } from './action';
import { ConditionGroup, and, getExpensesValue } from './conditions';
import { getNode, isRight, isVerticalGroup } from './node';
import { Version } from './waterfall';

export const stateInitDate = new Date(1); // 01/01/1970

export interface NodeState {
  id: string;
  turnover: AmountState;
  revenu: AmountState;
  percent: number;
  previous: string[];
}

export interface RightState extends NodeState {
  orgId: OrgState['id'];
  contractId?: ContractState['id'];
  enabled: boolean;
  conditions: ConditionGroup;
  pools: string[];
  revenu: RevenuState;
  turnover: TurnoverState;
  shadow: ShadowState;
}

export interface GroupState extends NodeState {
  children: string[];
  turnover: TurnoverState;
}

export type VerticalState = GroupState;

export interface HorizontalState extends GroupState {
  blameId: string;
}

interface CreateRight {
  id: RightState['id'];
  orgId: OrgState['id'];
  contractId?: ContractState['id'];
  percent: number;
  conditions?: ConditionGroup;
  previous?: string[];
  pools?: string[];
}

export function createRightState(right: CreateRight): RightState {
  const id = right.id ?? `right_${Math.round(Math.random() * 10000)}`;
  return {
    revenu: {
      calculated: 0,
      actual: 0,
      history: {}
    },
    turnover: {
      calculated: 0,
      actual: 0,
      history: {}
    },
    shadow: {
      revenu: 0,
      history: {}
    },
    previous: [],
    conditions: and([]),
    pools: [],
    enabled: true,
    ...right,
    id,
  }
}

interface CreateGroup {
  id: string;
  children: GroupChild[];
  previous?: string[];
  percent?: number;
}

type CreateVertical = CreateGroup;

interface CreateHorizontal extends CreateGroup {
  blameId: string;
}

export function createHorizontal(group: CreateHorizontal): HorizontalState {
  const { id, children, previous, percent } = group;
  return {
    id,
    percent: percent ?? 1,
    revenu: {
      calculated: 0,
      actual: 0
    },
    turnover: {
      calculated: 0,
      actual: 0,
      history: {}
    },
    previous: previous ?? [],
    children: children.map(child => child.id),
    blameId: group.blameId
  }
}

export function createVertical(group: CreateVertical): VerticalState {
  const { id, children, previous, percent } = group;
  const childOrgs = Array.from(new Set(children.filter(c => c.type === 'right').map((c: any) => c.orgId)));
  if (childOrgs.length > 1) throw new Error(`Childrens of vertical group "${group.id}" must have the same organizations`);
  if (!childOrgs[0]) throw new Error(`Childrens of vertical group "${group.id}" must have an organization`);
  return {
    id,
    percent: percent ?? 1,
    revenu: {
      calculated: 0,
      actual: 0
    },
    turnover: {
      calculated: 0,
      actual: 0,
      history: {}
    },
    previous: previous ?? [],
    children: children.map(child => child.id),
  }
}

export type OperationType = 'income' | 'investment';
export interface Operation {
  amount: number;
  type: OperationType;
  contractId?: string;
  date: Date;
}

interface AmountState {
  calculated: number;
  actual: number;
}

interface RevenuState extends AmountState {
  history: { [incomeId: string]: number } // Right Node revenue per income
}

interface TurnoverState extends AmountState {
  history: { [incomeId: string]: number } // Pool turnover per income
}

interface ShadowState {
  revenu: number;
  history: { [incomeId: string]: number } // Shadow revenue per income
}

export interface OrgState {
  id: string;
  turnover: AmountState;
  revenu: AmountState;
  investment: number;
  bonus: number;
  operations: Operation[];
}
export const createOrg = (org: Partial<OrgState>): OrgState => ({
  id: `org_${Math.round(Math.random() * 10000)}`,
  turnover: {
    calculated: 0,
    actual: 0
  },
  revenu: {
    calculated: 0,
    actual: 0
  },
  investment: 0,
  bonus: 0,
  operations: [],
  ...org
});

export interface PoolState {
  id: string;
  revenu: AmountState;
  turnover: TurnoverState;
  shadow: ShadowState;
}

export const createPoolState = (pool: Partial<PoolState>): PoolState => ({
  id: '',
  turnover: {
    calculated: 0,
    actual: 0,
    history: {}
  },
  revenu: {
    calculated: 0,
    actual: 0
  },
  shadow: {
    revenu: 0,
    history: {}
  },
  ...pool
});

export interface IncomeState {
  id: string;
  date: Date;
  from: string;
  to: string;
  amount: number;
  territories: Territory[];
  medias: Media[];
  isCompensation?: boolean;
  contractId?: string;
}

export interface ExpenseState {
  id: string;
  orgId: string;
  date: Date;
  amount: number;
  typeId: string;
  capped: boolean;
}
export function createExpenseState(expense: Partial<ExpenseState> & { id: string }): ExpenseState {
  return {
    orgId: '',
    date: new Date(),
    typeId: '',
    amount: 0,
    capped: false,
    ...expense
  }
}

interface ExpenseTypeState {
  id: string;
  name: string;
  cap: number;
}
export function createExpenseTypeState(expense: Partial<ExpenseTypeState> & { id: string }): ExpenseTypeState {
  return {
    name: '',
    cap: 0,
    ...expense
  }
}

type PaymentState = PaymentAction;

export interface ContractState {
  id: string;
  date?: Date; // Signature date
  amount: number;
  start?: Date;
  end?: Date;
}
export function createContractState(contract: Partial<ContractState> & { id: string }): ContractState {
  return {
    amount: 0,
    ...contract
  }
}

export interface SourceState {
  id: string;
  amount: number;
  destinationId: string;
  incomeIds: string[];
  shadow: boolean;
}

export function createSourceState(source: Partial<SourceState>): SourceState {
  return {
    id: '',
    amount: 0,
    destinationId: '',
    incomeIds: [],
    shadow: false,
    ...source,
  }
}

export interface AmortizationState {
  id: string;
  filmCost: number;
  financing: number; // TODO #10129 state.investment could be used instead (in the future)
  poolId: string;
}

export function createAmortizationState(amortization: Partial<AmortizationState>): AmortizationState {
  return {
    id: '',
    filmCost: 0,
    financing: 0,
    poolId: '',
    ...amortization,
  }
}

export interface TransferState {
  /** from->to */
  id: `${string}->${string}`;
  from: string;
  to: string;
  amount: number;
  history: {
    /** The income source */
    incomeId: string;
    /** The amount transfered */
    amount: number;
    /** Was the conditions valid for transfer */
    checked: boolean;
    /** Percent that "to" taken from "amount" (this allow percentage history if right is updated) */
    percent: number;
  }[];
}
export function createTransfer(id: `${string}->${string}`): TransferState {
  const [from, to] = id.split('->');
  return {
    id,
    from,
    to,
    amount: 0,
    history: [],
  }
}

export interface BonusState {
  amount: number;
  groupId: string;
  incomeId: string;
  orgId: string;
}

export const createBonus = (bonus: Partial<BonusState>): BonusState => ({
  amount: 0,
  incomeId: '',
  groupId: '',
  orgId: '',
  ...bonus
});

export interface WaterfallState {
  waterfall: { state: TitleState; history: History[] };
  version: Version;
}

export interface TitleState {
  id: string;
  date: Date;
  investment: number; // TODO #10129 Not used right now but could be used for amortization instead/in addition of putting a manual "financing" value
  expenseTypes: {
    [id: string]: ExpenseTypeState;
  }
  declarations: {
    [key: `${string}->${string}`]: number;
  },
  transfers: {
    [transferId: `${string}->${string}`]: TransferState;
  };
  incomes: {
    [id: string]: IncomeState;
  }
  expenses: {
    [id: string]: ExpenseState;
  }
  contracts: {
    [id: string]: ContractState;
  }
  events: {
    [eventId: string]: number | true;
  }
  onEvents: {
    [eventId: string]: Action[];
  }
  orgs: {
    [orgId: string]: OrgState;
  }
  horizontals: {
    [groupId: string]: HorizontalState;
  }
  verticals: {
    [groupId: string]: VerticalState;
  }
  rights: {
    [rightId: string]: RightState;
  }
  pools: {
    [poolId: string]: PoolState
  },
  bonuses: Record<`${IncomeState['id']}-${VerticalState['id']}`, BonusState>,
  sources: {
    [sourceId: string]: SourceState
  },
  payments: {
    [id: string]: PaymentState;
  },
  amortizations: {
    [amortizationId: string]: AmortizationState
  },
}

export interface History extends TitleState {
  actions: Action[];
  name: string; // Block name
}

export const createTitleState = (id: string): TitleState => ({
  id,
  date: new Date(0), // 1970: will be updated by the actions
  expenseTypes: {},
  investment: 0,
  declarations: {},
  transfers: {},
  incomes: {},
  expenses: {},
  contracts: {},
  events: {},
  onEvents: {},
  orgs: {},
  rights: {},
  horizontals: {},
  verticals: {},
  pools: {},
  bonuses: {},
  sources: {},
  payments: {},
  amortizations: {},
});

/**
 * Get total incomes of the state
 * @param state 
 * @param sourcesIds filter incomes by sources ids if provided
 * @returns 
 */
export function getTotalIncomes(state: TitleState, sourcesIds: string[] = []) {
  const sourceStates = sourcesIds.length ? Object.values(state.sources).filter(s => sourcesIds.includes(s.id)) : Object.values(state.sources);
  const notShadowIncomeIds = sourceStates.filter(s => !s.shadow).map(s => s.incomeIds).flat();
  const incomeStates = Object.values(state.incomes)
    .filter(i => notShadowIncomeIds.includes(i.id))
    .filter(i => sourceStates.some(s => s.incomeIds.includes(i.id)));
  if (!incomeStates.length) return 0;
  return incomeStates.map(a => a.amount).reduce((a, b) => a + b);
}

export function getTotalExpenses(state: TitleState, orgIds: string[] = []) {
  let total = 0;
  for (const typeId of Object.keys(state.expenseTypes)) {
    const expenses = orgIds.length ? Object.values(state.expenses).filter(e => e.typeId === typeId && orgIds.includes(e.orgId)) : Object.values(state.expenses).filter(e => e.typeId === typeId);
    if (!expenses.length) continue;
    const expensesValue = getExpensesValue(state, typeId);
    total += expensesValue;
  }
  return total;
}

export function getNodeTurnover(state: TitleState, sourceId: string, id: string) {
  const incomeIds = state.sources[sourceId].incomeIds;

  const node = getNode(state, id);
  if (isRight(state, node)) return sum(incomeIds.map(id => state.rights[node.id]?.turnover.history[id] ?? 0));
  if (isVerticalGroup(state, node)) return sum(incomeIds.map(id => state.verticals[node.id]?.turnover.history[id] ?? 0));
  return sum(incomeIds.map(id => state.horizontals[node.id]?.turnover.history[id] ?? 0));
}
